import React, { useContext, useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import { makeStyles, createTheme, ThemeProvider } from "@material-ui/core/styles";
import { IconButton } from "@material-ui/core";
import { MoreVert, Replay } from "@material-ui/icons";
import Button from "@material-ui/core/Button";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import TicketOptionsMenu from "../TicketOptionsMenu";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import UndoRoundedIcon from '@material-ui/icons/UndoRounded';
import MarkunreadIcon from '@material-ui/icons/Markunread';
import Tooltip from '@material-ui/core/Tooltip';
import { green, red } from '@material-ui/core/colors';
import ForwardIcon from '@material-ui/icons/Forward';
import SmartToyIcon from '@material-ui/icons/EmojiObjects'; // AI icon (EmojiObjects as fallback)
import TagsContainer from "../TagsContainer";
import FollowUpToggle from "../FollowUpToggle";
import MoveTicketSectionButton from "../MoveTicketSectionButton";
import VoiceCallButton from "../VoiceCallButton";
import QuickChargeButton from "../QuickChargeButton";
import ErrorBoundary from "../ErrorBoundary";
import { toast } from "react-toastify";

const useStyles = makeStyles(theme => ({
	actionButtons: {
		marginRight: 6,
		marginLeft: 6,
		display: "flex",
		alignSelf: "center",
		alignItems: "center",
		justifyContent: 'flex-start',
		flexWrap: 'nowrap',
		gap: theme.spacing(0.5),
		"& > *": {
			margin: theme.spacing(0.25),
		},
		"& .MuiIconButton-root": {
			padding: theme.spacing(0.75),
		},
		// Mobile polish — applied to ALL ticket statuses, not just
		// open. Compact pill-style icon buttons, even sizing, no
		// margin gaps, dense Accept button. This used to live under
		// the open-only modifier and pending tickets were stuck with
		// the bare desktop layout on phones.
		[theme.breakpoints.down('xs')]: {
			margin: 0,
			padding: '4px 8px',
			width: '100%',
			gap: 6,
			justifyContent: 'flex-start',
			backgroundColor: theme.palette.background.paper,
			borderBottom: `1px solid ${theme.palette.divider}`,
			"& > *": {
				flexShrink: 0,
				margin: '0 !important',
			},
			"& .MuiFormControl-root": {
				margin: '0 !important',
			},
			"& .MuiButton-containedPrimary": {
				height: 32,
				padding: '4px 12px',
				fontSize: '0.85rem',
				fontWeight: 600,
				borderRadius: 16,
				textTransform: 'none',
				minWidth: 'unset',
			},
			"& .MuiIconButton-root": {
				width: 32,
				height: 32,
				padding: 4,
				borderRadius: 16,
				// grey[100] is a STATIC light grey - it does not flip with the theme,
				// so in dark mode these pills rendered as white circles.
				backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : theme.palette.grey[100],
				'& .MuiSvgIcon-root': { fontSize: '1.2rem' },
			},
			"& .MuiOutlinedInput-root": {
				height: 32,
				borderRadius: 16,
				// grey[100] is a STATIC light grey - it does not flip with the theme,
				// so in dark mode these pills rendered as white circles.
				backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : theme.palette.grey[100],
				'& .MuiOutlinedInput-notchedOutline': { border: 'none' },
			},
			"& .MuiSelect-outlined": {
				padding: '4px 10px',
				paddingRight: 26,
				fontSize: '0.8rem',
			},
		},
	},
	// Scroll fallback for the action bar — applied for EVERY ticket
	// status (was open-only, which left pending/closed rows clipped:
	// tags + toggles + Accept/Reopen overflowed the nowrap row and
	// the trailing button became unreachable on phones).
	actionButtonsOpenScroll: {
		minWidth: 0,
		maxWidth: '100%',
		overflowX: 'auto',
		WebkitOverflowScrolling: 'touch',
		// Hide the native scrollbar everywhere — Raviv flagged it
		// as visually noisy on desktop. The row is still scrollable
		// by drag/wheel when there's overflow, just without the
		// chunky bar across the bottom of the action strip.
		scrollbarWidth: 'none',
		"&::-webkit-scrollbar": { display: 'none' },
		"& > *": { flexShrink: 0 },
	},
	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
}));

const TicketActionButtonsCustom = ({ ticket, showTabGroups, showSelectMessageCheckbox, 
	selectedMessages, 
	forwardMessageModalOpen,
	setForwardMessageModalOpen,
	onTicketUpdate }) => {
	const classes = useStyles();
	const history = useHistory();
	const [anchorEl, setAnchorEl] = useState(null);
	const [loading, setLoading] = useState(false);
	const ticketOptionsMenuOpen = Boolean(anchorEl);
	const { user } = useContext(AuthContext);
	const { setCurrentTicket } = useContext(TicketsContext);
	// Instant-switch safety: during a chat switch the PREVIOUS ticket stays
	// mounted (kept visible until the new one loads), so for a moment the
	// route uuid points at the NEW ticket while `ticket` still holds the old
	// one. Acting in that window (accept/resolve/return/AI/unread) would
	// mutate the wrong customer's ticket — every mutating handler below
	// bails while the mounted ticket lags the route.
	const { ticketId: routeUuid } = useParams();
	const ticketLagsRoute = Boolean(
		routeUuid && ticket?.uuid && ticket.uuid !== routeUuid
	);

	const customTheme = createTheme({
		palette: {
		  	primary: green,
		}
	});

	const handleOpenTicketOptionsMenu = e => {
		setAnchorEl(e.currentTarget);
	};

	const handleCloseTicketOptionsMenu = e => {
		setAnchorEl(null);
	};
	const handleOpenModalForward = () => {
		if (selectedMessages.length === 0) {
			toastError({response: {data: {message: 	i18n.t("forwardMessageModal.dialog.error")		}}});
			return;
		}
		setForwardMessageModalOpen(true);
	}
	const handleUpdateTicketStatus = async (e, status, userId) => {
		// See ticketLagsRoute above — never mutate a ticket that lags the route.
		if (ticketLagsRoute) return;
		setLoading(true);
		try {
			const { data: updatedTicket } = await api.put(`/tickets/${ticket.id}`, {
				status: status,
				userId: userId || null,
			});

			setLoading(false);
			
			// Immediately update local ticket state for instant UI refresh
			if (onTicketUpdate && updatedTicket) {
				onTicketUpdate(updatedTicket);
			}
			
			if (status === "open") {
				setCurrentTicket({ ...updatedTicket, code: "#open" });
			} else {
				setCurrentTicket({ id: null, code: null })
				history.push("/tickets");
			}
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleToggleAI = async () => {
		// See ticketLagsRoute above — never mutate a ticket that lags the route.
		if (ticketLagsRoute) return;
		setLoading(true);
		try {
			const newDisableAI = !ticket.disableAI;
			const { data: updatedTicket } = await api.put(`/tickets/${ticket.id}`, {
				disableAI: newDisableAI,
			});

			setLoading(false);

			if (onTicketUpdate && updatedTicket) {
				onTicketUpdate(updatedTicket);
			}

			toast.success(newDisableAI
				? i18n.t("messagesList.header.buttons.disableAI")
				: i18n.t("messagesList.header.buttons.enableAI"));
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
	};

	const handleMarkAsUnread = async () => {
		// See ticketLagsRoute above — never mutate a ticket that lags the route.
		if (ticketLagsRoute) return;
		try {
			await api.put(`/tickets/${ticket.id}/unread`);
			// Navigate back to the ticket list — the yellow badge will appear there
			history.push("/tickets");
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={`${classes.actionButtons} ${classes.actionButtonsOpenScroll}`}>
			{/* Three-dots menu (Schedule / Transfer / Reset Chatbot /
			    Request Payment / Import History / Download / Delete)
			    rendered first so it stays in the visible region of the
			    action bar (overflow-x: auto on the container). Same
			    gating as the legacy placement: open tickets, plus
			    group tickets when the agent is in the groups tab.
			    Pending / closed don't show it — matches pre-refactor
			    behaviour. */}
			<TagsContainer ticket={ticket} />
			<FollowUpToggle ticket={ticket} />
			{/* Voice call button + QuickCharge removed from the header per the
			    agent's workflow — calling lives in the composer (bottom) bar, and
			    QuickCharge stays available in the call Outcome panel + timeline. */}
			{/* Unified destination picker: lets the agent move the ticket to
			    any section (built-in status or custom tag-driven). The
			    Pending → Open "Accept" button below is intentionally kept
			    so the most common action stays a single click. */}
			{ticket?.status !== "pending" && (
				<MoveTicketSectionButton ticket={ticket} onTicketUpdate={onTicketUpdate} />
			)}
			{/* AI Toggle Button - visible when ticket is open or pending */}
			{(ticket.status === "open" || ticket.status === "pending") && (
				<Tooltip title={ticket.disableAI 
					? i18n.t("messagesList.header.buttons.enableAI") 
					: i18n.t("messagesList.header.buttons.disableAI")}>
					<IconButton 
						onClick={handleToggleAI}
						style={{ color: ticket.disableAI ? red[500] : green[500], padding: 6 }}
					>
						<SmartToyIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			)}
			{ticket.status === "closed" && (!showTabGroups || !ticket.isGroup) && (
				<ButtonWithSpinner
					loading={loading}
					startIcon={<Replay />}
					size="small"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.reopen")}
				</ButtonWithSpinner>
			)}
						{/* Mark as Unread — visible for all statuses (open / pending / closed).
			    Sets unreadMessages=1 via the backend so the yellow badge reappears
			    in the ticket list. Navigates back to /tickets after marking. */}
			<Tooltip title={i18n.t("messagesList.header.buttons.markAsUnread")}>
				<IconButton onClick={handleMarkAsUnread} size="small">
					<MarkunreadIcon fontSize="small" />
				</IconButton>
			</Tooltip>

						{!showSelectMessageCheckbox ? (
							<>

			{(ticket.status === "open" || (showTabGroups && ticket.isGroup ) ) && (
				<>
          {(!showTabGroups || !ticket.isGroup) &&
            <>
              <Tooltip title={i18n.t("messagesList.header.buttons.return")}>
                <IconButton onClick={e => handleUpdateTicketStatus(e, "pending", null)}>
                  <UndoRoundedIcon />
                </IconButton>
              </Tooltip>
              <ThemeProvider theme={customTheme}>
                <Tooltip title={i18n.t("messagesList.header.buttons.resolve")}>
                  <IconButton onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)} color="primary">
                    <CheckCircleIcon />
                  </IconButton>
                </Tooltip>
              </ThemeProvider>
            </>
          }
					{/* <ButtonWithSpinner
						loading={loading}
						startIcon={<Replay />}
						size="small"
						onClick={e => handleUpdateTicketStatus(e, "pending", null)}
					>
						{i18n.t("messagesList.header.buttons.return")}
					</ButtonWithSpinner>
					<ButtonWithSpinner
						loading={loading}
						size="small"
						variant="contained"
						color="primary"
						onClick={e => handleUpdateTicketStatus(e, "closed", user?.id)}
					>
						{i18n.t("messagesList.header.buttons.resolve")}
					</ButtonWithSpinner> */}
					<IconButton onClick={handleOpenTicketOptionsMenu}>
						<MoreVert />
					</IconButton>
					<TicketOptionsMenu
						ticket={ticket}
						anchorEl={anchorEl}
						menuOpen={ticketOptionsMenuOpen}
						handleClose={handleCloseTicketOptionsMenu}
						showTabGroups={showTabGroups}
					/>
				</>
			)}
			</>
		): (
			<Tooltip title={i18n.t("messageOptionsMenu.forwardbutton")}>
				<span>
			<Button
				loading={loading}
			  variant="contained"
			  color="primary"
			  onClick={handleOpenModalForward}
			>
			  <ForwardIcon/>
			</Button>
			</span>
		  </Tooltip>
			)	}
			{ticket.status === "pending" && (!showTabGroups || !ticket.isGroup) && (
				<span>
				<ButtonWithSpinner
					loading={loading}
					size="small"
					variant="contained"
					color="primary"
					onClick={e => handleUpdateTicketStatus(e, "open", user?.id)}
				>
					{i18n.t("messagesList.header.buttons.accept")}
				</ButtonWithSpinner>
				</span>
			)}
		</div>
	);
};

export default TicketActionButtonsCustom;
